import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { apiHost } from '../../utils/apiHost';
import { buatBarisShift, formatWaktuShift, opsiDariKonfigurasi } from '../../struk/strukLayout';
import { ambilKonfigStruk } from '../../struk/strukWeb';
import { cetakBrowser } from '../../struk/cetakStruk';
import { waktuCabang } from '../../utils/waktuCabang';
import { tingkatShift, teksUmur, umurJam } from '../../utils/shiftWaktu';

// Shift kasir di web kasir (server: controller/shiftController.js). Sama dengan aplikasi: wajib buka shift
// sebelum menerima pembayaran, kas masuk/keluar, tutup "buta" (kasir tidak melihat angka seharusnya).

const base = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const ambil = (url) => axios.get(url, { withCredentials: true }).then((r) => r.data);
const rupiah = (v) => `Rp ${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;
const jam = (iso) => (iso ? new Date(iso).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-');
const angka = (s) => Number(String(s || '').replace(/[^0-9]/g, '')) || 0;
const ribuan = (s) => { const n = angka(s); return n ? n.toLocaleString('id-ID') : ''; };
const pesanGalat = (e) => e?.response?.data?.message || e?.message || 'Gagal';

// Web kasir ini sudah punya layar shift: server mewajibkan shift untuk transaksi & konfirmasi bayar.
let interceptorTerpasang = false;
if (!interceptorTerpasang) {
  interceptorTerpasang = true;
  axios.interceptors.request.use((cfg) => {
    if (/createtransaksicabang|confirmcashpayment|\/piutang\/[^/]+\/bayar/.test(cfg.url || '')) {
      cfg.headers = cfg.headers || {};
      cfg.headers['X-Kasir-Shift'] = '1';
    }
    return cfg;
  });
}

/** Shift kasir yang sedang buka: { shift, memuat, muatUlang }. */
export const useShiftKasir = (aktif = true) => {
  // aktif=false (bukan akun kasir): jangan minta shift ke server (server hanya melayani akun kasir).
  const { data, isLoading, mutate } = useSWR(aktif ? `${base()}/shift/aktif` : null, ambil, { revalidateOnFocus: true });
  const shift = data?.data || null;
  // Shift lupa ditutup: umur dihitung ulang tiap menit supaya peringatan / penguncian muncul sendiri tanpa memuat ulang halaman.
  const [sekarang, setSekarang] = useState(() => Date.now());
  useEffect(() => {
    if (!shift) return undefined;
    setSekarang(Date.now());
    const id = setInterval(() => setSekarang(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, [shift]);
  const tingkat = shift ? tingkatShift(shift, sekarang) : 'normal';
  return { shift, memuat: isLoading, muatUlang: mutate, tingkat, terkunci: tingkat === 'terkunci', umur: shift ? umurJam(shift.dibukaAt, sekarang) : 0 };
};

export const DialogBukaShift = ({ open, onClose, onDibuka, dariPembayaran }) => {
  const [kas, setKas] = useState('');
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');
  const buka = async () => {
    setProses(true); setGalat('');
    try {
      await axios.post(`${base()}/shift/buka`, { kasAwal: angka(kas) }, { withCredentials: true });
      setKas('');
      onDibuka?.();
      onClose();
    } catch (e) {
      // Sudah dibuka di perangkat lain (mis. aplikasi HP): shift itu yang dipakai.
      if (e?.response?.data?.kode === 'SHIFT_MASIH_BUKA') { onDibuka?.(); onClose(); return; }
      setGalat(pesanGalat(e));
    } finally {
      setProses(false);
    }
  };
  return (
    <Dialog open={open} onClose={() => !proses && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Buka shift</DialogTitle>
      <DialogContent>
        {dariPembayaran && <Alert severity="info" sx={{ mb: 2 }}>Buka shift dulu sebelum menerima pembayaran.</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Hitung uang modal yang ada di laci sekarang.</Typography>
        <TextField autoFocus fullWidth label="Kas awal" value={ribuan(kas)} onChange={(e) => setKas(e.target.value)}
          InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>Rp</Typography> }} placeholder="0"
          onKeyDown={(e) => e.key === 'Enter' && buka()} disabled={proses} />
        {galat && <Alert severity="error" sx={{ mt: 2 }}>{galat}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={proses}>Batal</Button>
        <Button variant="contained" onClick={buka} disabled={proses}>{proses ? 'Membuka…' : 'Buka shift'}</Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogKas = ({ open, onClose, onTersimpan }) => {
  const [jenis, setJenis] = useState('keluar');
  const [jumlah, setJumlah] = useState('');
  const [catatan, setCatatan] = useState('');
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');
  const simpan = async () => {
    if (!angka(jumlah)) return setGalat('Isi jumlah uang');
    if (catatan.trim().length < 3) return setGalat('Tulis catatan (mis. beli es batu, setor ke pemilik)');
    setProses(true); setGalat('');
    try {
      await axios.post(`${base()}/shift/kas`, { jenis, jumlah: angka(jumlah), catatan: catatan.trim() }, { withCredentials: true });
      setJumlah(''); setCatatan('');
      onTersimpan?.(jenis === 'masuk' ? 'Kas masuk dicatat' : 'Kas keluar dicatat');
      onClose();
    } catch (e) {
      setGalat(pesanGalat(e));
    } finally {
      setProses(false);
    }
  };
  return (
    <Dialog open={open} onClose={() => !proses && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Kas masuk / keluar</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <ToggleButtonGroup exclusive fullWidth value={jenis} onChange={(_, v) => v && setJenis(v)} disabled={proses}>
          <ToggleButton value="keluar">Kas keluar</ToggleButton>
          <ToggleButton value="masuk">Kas masuk</ToggleButton>
        </ToggleButtonGroup>
        <TextField label="Jumlah" value={ribuan(jumlah)} onChange={(e) => setJumlah(e.target.value)} disabled={proses}
          InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>Rp</Typography> }} />
        <TextField label="Catatan" value={catatan} onChange={(e) => setCatatan(e.target.value)} disabled={proses} inputProps={{ maxLength: 200 }}
          placeholder={jenis === 'keluar' ? 'mis. beli es batu, setor ke pemilik' : 'mis. tambah uang kembalian'} />
        {galat && <Alert severity="error">{galat}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={proses}>Batal</Button>
        <Button variant="contained" onClick={simpan} disabled={proses}>Simpan</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Ringkasan hasil tutup shift (dipakai juga laporan admin). */
export const RincianShift = ({ h }) => {
  const r = h?.ringkasan || {};
  const m = r.metode || {};
  const selisih = Number(h?.selisih) || 0;
  const warna = h?.selisih === null || h?.selisih === undefined ? 'text.secondary' : selisih === 0 ? 'success.main' : selisih < 0 ? 'error.main' : 'warning.dark';
  const Baris = ({ a, b, tebal }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
      <Typography variant="body2" fontWeight={tebal ? 700 : 400}>{a}</Typography>
      <Typography variant="body2" fontWeight={tebal ? 700 : 500}>{b}</Typography>
    </Box>
  );
  return (
    <Box>
      <Box sx={{ textAlign: 'center', p: 2, mb: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
        <Typography color={warna} fontWeight={700}>
          {h?.selisih === null || h?.selisih === undefined ? 'Tanpa hitungan kas' : selisih === 0 ? 'Kas pas' : selisih < 0 ? 'Kas kurang' : 'Kas lebih'}
        </Typography>
        {h?.selisih !== null && h?.selisih !== undefined && (
          <Typography variant="h4" color={warna} fontWeight={700}>{selisih < 0 ? '−' : selisih > 0 ? '+' : ''}{rupiah(Math.abs(selisih))}</Typography>
        )}
      </Box>
      <Baris a="Dibuka" b={jam(h?.dibukaAt)} />
      <Baris a="Ditutup" b={jam(h?.ditutupAt)} />
      <Divider sx={{ my: 1 }} />
      <Baris a="Kas awal" b={rupiah(h?.kasAwal)} />
      <Baris a={`Penjualan tunai (${m.cash?.jumlah || 0})`} b={rupiah(m.cash?.total)} />
      {r.pelunasan?.tunai > 0 && <Baris a={`Pelunasan piutang tunai (${r.pelunasan.jumlah})`} b={rupiah(r.pelunasan.tunai)} />}
      {r.pelunasan?.dikembalikanTunai > 0 && <Baris a="Pengembalian piutang tunai" b={`−${rupiah(r.pelunasan.dikembalikanTunai)}`} />}
      <Baris a="Kas masuk" b={rupiah(r.kasMasuk)} />
      <Baris a="Kas keluar" b={`−${rupiah(r.kasKeluar)}`} />
      <Baris a="Kas seharusnya" b={rupiah(h?.kasSeharusnya)} tebal />
      <Baris a="Uang dihitung" b={h?.kasDihitung === null || h?.kasDihitung === undefined ? '-' : rupiah(h?.kasDihitung)} tebal />
      <Divider sx={{ my: 1 }} />
      {m.qris?.jumlah > 0 && <Baris a={`QRIS (${m.qris.jumlah})`} b={rupiah(m.qris.total)} />}
      {m.qris_statis?.jumlah > 0 && <Baris a={`QRIS statis (${m.qris_statis.jumlah})`} b={rupiah(m.qris_statis.total)} />}
      {m.noncash?.jumlah > 0 && <Baris a={`Non-tunai (${m.noncash.jumlah})`} b={rupiah(m.noncash.total)} />}
      {r.pelunasan?.nonTunai > 0 && <Baris a="Pelunasan piutang non-tunai" b={rupiah(r.pelunasan.nonTunai)} />}
      <Baris a={`Total penjualan (${r.jumlahTransaksi || 0} transaksi)`} b={rupiah(r.penjualan)} tebal />
      {r.piutangBaru?.jumlah > 0 && <Baris a={`Piutang baru (${r.piutangBaru.jumlah})`} b={rupiah(r.piutangBaru.total)} />}
      {r.batal?.jumlah > 0 && <Baris a={`Dibatalkan (${r.batal.jumlah})`} b={rupiah(r.batal.total)} />}
      {r.pending?.jumlah > 0 && <Baris a={`Belum lunas (${r.pending.jumlah})`} b={rupiah(r.pending.total)} />}
      {(r.kas || []).length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2">Kas masuk / keluar</Typography>
          {r.kas.map((k) => <Baris key={k.uuid} a={`${k.jenis === 'masuk' ? 'Masuk' : 'Keluar'} · ${k.catatan}`} b={`${k.jenis === 'masuk' ? '' : '−'}${rupiah(k.jumlah)}`} />)}
        </>
      )}
      {h?.catatan && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Catatan: {h.catatan}</Typography>}
    </Box>
  );
};

/** Cetak struk tutup shift lewat jendela cetak browser; tata letak SAMA dengan struk shift di Flutter (struk/strukLayout.js). */
export const cetakStrukShift = async (h, namaKasir) => {
  const waktu = (iso) => (iso ? formatWaktuShift(waktuCabang(iso)) : '-');
  // Struk shift hanya mengikuti lebar kertas yang diatur cabang (menu Struk).
  const { cabangConfig } = await ambilKonfigStruk(axios, base(), h?.cabanguuid || h?.cabang?.uuid);
  cetakBrowser(buatBarisShift({ kasir: namaKasir || '-', buka: waktu(h?.dibukaAt), tutup: waktu(h?.ditutupAt), h: h || {}, kolom: opsiDariKonfigurasi(cabangConfig).kolom }));
  return true;
};

const DialogTutup = ({ open, onClose, shift, onDitutup }) => {
  const [uang, setUang] = useState('');
  const [catatan, setCatatan] = useState('');
  const [konfirmasi, setKonfirmasi] = useState(false);
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');
  const tutup = async () => {
    setProses(true); setGalat('');
    try {
      const r = await axios.post(`${base()}/shift/tutup`, { kasDihitung: angka(uang), catatan: catatan.trim() }, { withCredentials: true });
      setUang(''); setCatatan(''); setKonfirmasi(false);
      onDitutup(r.data?.data);
      onClose();
    } catch (e) {
      setGalat(pesanGalat(e));
      setKonfirmasi(false);
    } finally {
      setProses(false);
    }
  };
  return (
    <Dialog open={open} onClose={() => !proses && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Tutup shift</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {shift && <Typography variant="body2" color="text.secondary">Dibuka {jam(shift.dibukaAt)} · kas awal {rupiah(shift.kasAwal)}</Typography>}
        <Typography fontWeight={600}>Hitung semua uang tunai di laci, lalu isi jumlahnya.</Typography>
        <TextField autoFocus label="Uang di laci" value={ribuan(uang)} onChange={(e) => { setUang(e.target.value); setKonfirmasi(false); }} disabled={proses}
          InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>Rp</Typography> }} placeholder="0" helperText="Selisih dengan catatan sistem terlihat setelah shift ditutup." />
        <TextField label="Catatan (opsional)" value={catatan} onChange={(e) => setCatatan(e.target.value)} disabled={proses} multiline minRows={2} inputProps={{ maxLength: 500 }} />
        {konfirmasi && <Alert severity="warning">Tutup shift dengan uang di laci {rupiah(angka(uang))}? Transaksi berikutnya butuh shift baru.</Alert>}
        {galat && <Alert severity="error">{galat}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={proses}>Batal</Button>
        <Button variant="contained" disabled={proses || uang === ''} onClick={() => (konfirmasi ? tutup() : setKonfirmasi(true))}>
          {proses ? 'Menutup…' : konfirmasi ? 'Ya, tutup shift' : 'Tutup shift'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Tutup shift + hasilnya (kas seharusnya, selisih, cetak struk). Dipakai kartu shift di halaman kasir dan alur "tutup shift lalu keluar".
 * onSelesai dipanggil setelah kasir menekan "Selesai" pada hasil.
 */
export const DialogTutupShiftLengkap = ({ open, onClose, shift, namaKasir, onDitutup, onSelesai, onPesan }) => {
  const [hasil, setHasil] = useState(null);
  return (
    <>
      <DialogTutup open={open} onClose={onClose} shift={shift} onDitutup={(h) => { setHasil(h); onDitutup?.(h); }} />
      <Dialog open={!!hasil} onClose={() => { setHasil(null); onSelesai?.(); }} maxWidth="xs" fullWidth>
        <DialogTitle>Shift ditutup</DialogTitle>
        <DialogContent dividers><RincianShift h={hasil} /></DialogContent>
        <DialogActions>
          <Button onClick={() => !cetakStrukShift(hasil, namaKasir) && onPesan?.('Izinkan pop-up untuk mencetak struk')}>Cetak struk</Button>
          <Button variant="contained" onClick={() => { setHasil(null); onSelesai?.(); }}>Selesai</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * Kasir menekan logout saat shift masih terbuka: tawarkan menutup shift dulu (ganti kasir), keluar tanpa menutup, atau batal.
 * Shift yang lupa ditutup membuat laporan kas bercampur antar hari/kasir.
 */
export const DialogShiftSebelumKeluar = ({ open, shift, namaKasir, onBatal, onKeluar, onPesan, onDitutup }) => {
  const [tutup, setTutup] = useState(false);
  return (
    <>
      <Dialog open={open && !tutup} onClose={onBatal} maxWidth="xs" fullWidth>
        <DialogTitle>Shift masih terbuka</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {shift ? `Shift dibuka ${jam(shift.dibukaAt)} belum ditutup.` : 'Shift belum ditutup.'} Tutup shift sebelum keluar supaya uang di laci
            dihitung dan kasir berikutnya mulai dengan shift baru.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Button onClick={onBatal}>Batal</Button>
          <Button color="inherit" onClick={onKeluar}>Keluar tanpa tutup</Button>
          <Button variant="contained" onClick={() => setTutup(true)}>Tutup shift lalu keluar</Button>
        </DialogActions>
      </Dialog>
      <DialogTutupShiftLengkap open={open && tutup} onClose={() => setTutup(false)} shift={shift} namaKasir={namaKasir} onPesan={onPesan}
        onDitutup={(h) => { setTutup(false); onDitutup?.(h); }} onSelesai={onKeluar} />
    </>
  );
};

/**
 * Logout dengan pemeriksaan shift. mintaKeluar() dipanggil dari tombol logout: kasir dengan shift terbuka diberi pilihan dulu,
 * yang lain langsung keluar. Pasang `dialog` di pohon tampilan komponen pemakai.
 */
export const useKeluarCekShift = (adalahKasir, keluar, { namaKasir, onPesan } = {}) => {
  const { shift, muatUlang } = useShiftKasir(adalahKasir);
  const [buka, setBuka] = useState(false);
  const mintaKeluar = () => { if (adalahKasir && shift) setBuka(true); else keluar(); };
  const dialog = (
    <DialogShiftSebelumKeluar open={buka} shift={shift} namaKasir={namaKasir} onPesan={onPesan} onBatal={() => setBuka(false)}
      onDitutup={() => muatUlang()} onKeluar={() => { setBuka(false); keluar(); }} />
  );
  return { mintaKeluar, dialog, adaShift: !!shift };
};

/**
 * Kartu shift di halaman kasir web. Props mintaBuka/onMintaBukaSelesai: dibuka dari tombol Bayar saat
 * belum ada shift (lihat pemakaian di layoutProductPercabang & retail/index). mintaTutup/onMintaTutupSelesai: sama, untuk shift
 * yang terkunci karena terbuka lebih dari 24 jam (tutup dulu, hitung uang di laci).
 */
export const KartuShiftWeb = ({ shiftHook, mintaBuka = false, onMintaBukaSelesai, mintaTutup = false, onMintaTutupSelesai, namaKasir, onPesan }) => {
  const { shift, memuat, muatUlang, tingkat, umur } = shiftHook;
  const [buka, setBuka] = useState(false);
  const [kas, setKas] = useState(false);
  const [tutup, setTutup] = useState(false);
  const tutupBuka = () => { setBuka(false); onMintaBukaSelesai?.(); };
  const tutupTutup = () => { setTutup(false); onMintaTutupSelesai?.(); };
  return (
    <>
      {!memuat && !shift && (
        <Alert severity="warning" sx={{ mb: 1.5, alignItems: 'center' }}
          action={<Button variant="contained" color="warning" size="small" onClick={() => setBuka(true)}>Buka shift</Button>}>
          <b>Shift belum dibuka.</b> Buka shift dengan kas awal sebelum berjualan.
        </Alert>
      )}
      {shift && tingkat !== 'normal' && (
        <Alert severity={tingkat === 'terkunci' ? 'error' : 'warning'} sx={{ mb: 1.5, alignItems: 'center' }}
          action={<Button variant="contained" color={tingkat === 'terkunci' ? 'error' : 'warning'} size="small" onClick={() => setTutup(true)}>Tutup shift</Button>}>
          {tingkat === 'terkunci'
            ? <><b>Shift sudah terbuka {teksUmur(umur)}.</b> Transaksi baru dikunci. Tutup shift (hitung uang di laci), lalu buka shift baru.</>
            : <><b>Shift sudah terbuka {teksUmur(umur)}.</b> Tutup shift setelah selesai berjualan supaya kas tidak bercampur dengan hari berikutnya.</>}
        </Alert>
      )}
      {shift && (
        <Card variant="outlined" sx={{ mb: 1.5 }}>
          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 }, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip color="success" size="small" label="Shift buka" />
            <Typography variant="body2">
              Dibuka {jam(shift.dibukaAt)} · kas awal {rupiah(shift.kasAwal)}
              {shift.kasMasuk > 0 ? ` · masuk ${rupiah(shift.kasMasuk)}` : ''}{shift.kasKeluar > 0 ? ` · keluar ${rupiah(shift.kasKeluar)}` : ''}
            </Typography>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Button size="small" onClick={() => setKas(true)}>Kas masuk/keluar</Button>
              <Button size="small" variant="outlined" onClick={() => setTutup(true)}>Tutup shift</Button>
            </Box>
          </CardContent>
        </Card>
      )}
      <DialogBukaShift open={buka || mintaBuka} dariPembayaran={mintaBuka} onClose={tutupBuka} onDibuka={() => { muatUlang(); onPesan?.('Shift dibuka'); }} />
      <DialogKas open={kas} onClose={() => setKas(false)} onTersimpan={(p) => { muatUlang(); onPesan?.(p); }} />
      <DialogTutupShiftLengkap open={tutup || mintaTutup} onClose={tutupTutup} shift={shift} namaKasir={namaKasir} onPesan={onPesan}
        onDitutup={() => muatUlang()} />
    </>
  );
};
